// Saudi Riyal - Clean "SR" text label
//
// iter618 — Adds a singleton currency-symbol cache so any module
// (toasts, prompts, template literals, etc.) can render the tenant's
// currency symbol without prop-drilling `useTaxConfig` through every
// component. `<AppRoot>` (via useTaxConfig) calls `setTenantCurrency`
// once after the tax config loads; everything else just calls `cur()`
// synchronously.
let _ACTIVE_CURRENCY = (typeof window !== 'undefined' && window.localStorage?.getItem('smarto_tenant_currency')) || 'SAR';
export function setTenantCurrency(code) {
  if (!code) return;
  _ACTIVE_CURRENCY = String(code).toUpperCase();
  try { window.localStorage.setItem('smarto_tenant_currency', _ACTIVE_CURRENCY); } catch { /* */ }
}
export function getTenantCurrency() { return _ACTIVE_CURRENCY; }

export function RiyalIcon({ size = '1em', className = '' }) {
  return (
    <span
      className={`inline-block font-semibold ${className}`}
      style={{ fontSize: typeof size === 'string' ? size : `${size}px`, lineHeight: 1, letterSpacing: '-0.02em' }}
      aria-label="Saudi Riyal"
    >
      SR
    </span>
  );
}

// Currency symbols map
const CURRENCY_SYMBOLS = {
  SAR: 'SR', USD: '$', EUR: '\u20AC', GBP: '\u00A3', AED: 'AED',
  KWD: 'KWD', BHD: 'BHD', QAR: 'QAR', OMR: 'OMR', EGP: 'EGP',
  INR: '\u20B9', JPY: '\u00A5', CNY: '\u00A5', TRY: '\u20BA', PKR: 'Rs',
};

// Currency symbol constant for text-only contexts  
export const SAR_SYMBOL = 'SR';
export const CURRENCY_CODE = 'SAR';

// Get symbol for any currency code
export function getCurrencySymbol(code = 'SAR') {
  return CURRENCY_SYMBOLS[code] || code;
}

// iter618 — Tenant-aware shorthand. Use `cur()` in any string template
// to get the current tenant's currency symbol synchronously, e.g.
//   toast.success(`Refunded ${cur()} ${amount}`)
// `cur('USD')` returns the symbol for an explicit code.
export function cur(code) {
  return getCurrencySymbol(code || _ACTIVE_CURRENCY);
}

// Format a number with currency
export function formatSAR(amount, decimals = 2) {
  if (amount == null || isNaN(amount)) return '0';
  return Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// Format amount with any currency
export function formatCurrency(amount, currencyCode = 'SAR', decimals = 2) {
  if (amount == null || isNaN(amount)) return `${getCurrencySymbol(currencyCode)} 0`;
  const formatted = Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${getCurrencySymbol(currencyCode)} ${formatted}`;
}

// Reusable inline currency display: icon + formatted amount
export function CurrencyDisplay({ amount, decimals = 2, iconSize = '0.85em', className = '', currency = 'SAR' }) {
  const formatted = formatSAR(amount, decimals);
  const symbol = getCurrencySymbol(currency);
  return (
    <span className={`inline-flex items-center gap-0.5 ${className}`}>
      {currency === 'SAR' ? <RiyalIcon size={iconSize} /> : <span style={{ fontSize: iconSize }}>{symbol}</span>}
      <span>{formatted}</span>
    </span>
  );
}

// iter845r — Legacy aliases for the DRY consolidation. All new code
// should import `formatCurrency`; these are kept so we don't have to
// touch every one of the 48+ files simultaneously. Delete these once
// the migration is complete.
//
// fmt(amount)         → formatCurrency(amount, getTenantCurrency())
// fmtSR(amount)       → same, tenant-aware (was hardcoded to SR)
// money(amount)       → same
export function fmt(amount, decimals = 2) {
  return formatCurrency(amount, getTenantCurrency(), decimals);
}
export function fmtSR(amount, decimals = 2) {
  return formatCurrency(amount, getTenantCurrency(), decimals);
}
export function money(amount, decimals = 2) {
  return formatCurrency(amount, getTenantCurrency(), decimals);
}
